import { CommonModule } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  OnDestroy,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CnpjFormatter } from '@core/utils/cnpj.utils';
import { NoGrafoResponse } from '@interfaces/corporate-registry/responses/rede-societaria.response';
import { VisaoGeoResponse } from '@interfaces/corporate-registry/responses/rede-visoes.response';
import { injectOwnershipNetworkDisplay } from '@shared/components/ownership-network/ownership-network-display';
import type * as Leaflet from 'leaflet';
import {
  CORES_GRAFO,
  emAlerta,
} from '@shared/components/ownership-network/registry-graph-geometry';
import { resumoCustoGeo } from '@shared/components/ownership-network/registry-matrix-index';

/** Cor das linhas do `MapaGeoRede` do Dossiê. */
const COR_LINHA = '#93a4c4';

/**
 * Visão "Geo": empresas do recorte no mapa — réplica do `MapaGeoRede` do Vault-Dossiê (Leaflet +
 * tiles do OpenStreetMap), com o rodapé "Custo do mapa".
 *
 * ⚠️ O Leaflet acessa `window` ao ser importado: por isso entra por `import()` dentro de
 * `afterNextRender`, que não roda no SSR — e só pesa no bundle de quem abre esta visão.
 * ⚠️ O popup é montado com `textContent`, nunca com HTML: nome de empresa é dado externo.
 */
@Component({
  selector: 'app-registry-geo-map',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './registry-geo-map.component.html',
  styleUrl: './registry-geo-map.component.scss',
  // O CSS do Leaflet estiliza elementos que ele mesmo cria (fora do template), então não pode ser
  // encapsulado. As classes dele são todas `.leaflet-*`, e as nossas ficam sob `.registry-geo`.
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegistryGeoMapComponent implements OnDestroy {
  readonly geo = input.required<VisaoGeoResponse>();
  /** 558px no card; 100% em tela cheia (como o `altura` do Dossiê). */
  readonly altura = input<string>('558px');

  private readonly display = injectOwnershipNetworkDisplay();
  private readonly host = viewChild<ElementRef<HTMLDivElement>>('mapa');
  private L: typeof Leaflet | null = null;
  private mapa: Leaflet.Map | null = null;
  private camada: Leaflet.LayerGroup | null = null;

  protected readonly falhouCarregar = signal(false);
  private readonly pronto = signal(false);

  protected readonly pontos = computed(() =>
    this.geo().nos.filter(n => Number.isFinite(n.lat) && Number.isFinite(n.lng))
  );
  protected readonly custo = computed(() =>
    resumoCustoGeo(this.geo().custo, this.geo().semCoordenada)
  );

  constructor() {
    afterNextRender(() => {
      import('leaflet')
        .then(mod => {
          // O pacote é UMD: dependendo do bundler, o namespace vem em `default`.
          this.L = ((mod as unknown as { default?: typeof Leaflet }).default ??
            mod) as typeof Leaflet;
          this.pronto.set(true);
        })
        .catch(() => this.falhouCarregar.set(true));
    });

    effect(() => {
      const pontos = this.pontos();
      const linhas = this.geo().linhas;
      if (this.pronto()) this.desenhar(pontos, linhas);
    });
  }

  ngOnDestroy(): void {
    this.mapa?.remove();
    this.mapa = null;
  }

  private desenhar(pontos: NoGrafoResponse[], linhas: { origem: string; destino: string }[]): void {
    const L = this.L;
    const el = this.host()?.nativeElement;
    if (!L || !el || !pontos.length) return;

    if (!this.mapa) {
      this.mapa = L.map(el, { center: [-15.78, -47.93], zoom: 4, scrollWheelZoom: true });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      }).addTo(this.mapa);
      this.camada = L.layerGroup().addTo(this.mapa);

      // ⚠️ Sem o CSS do Leaflet o mapa não quebra com erro: os tiles saem da caixa e se
      // espalham pela página (visto em 25/09/2026 num dev server que já rodava antes de este
      // componente existir e nunca carregou a folha dele). O teste é o que o CSS garante: o
      // pane é `position: absolute`. Sem isso, melhor avisar do que desenhar o mapa em pedaços.
      const pane = this.mapa.getPane('mapPane');
      if (!pane || getComputedStyle(pane).position !== 'absolute') {
        this.mapa.remove();
        this.mapa = null;
        this.camada = null;
        this.falhouCarregar.set(true);
        return;
      }
    }
    const camada = this.camada!;
    camada.clearLayers();

    const porId = new Map(pontos.map(n => [n.id, n]));
    for (const l of linhas) {
      const a = porId.get(l.origem);
      const b = porId.get(l.destino);
      if (!a || !b) continue;
      L.polyline(
        [
          [a.lat!, a.lng!],
          [b.lat!, b.lng!],
        ],
        { color: COR_LINHA, weight: 1, opacity: 0.6 }
      ).addTo(camada);
    }

    const destacar = this.display.highlightRoot();
    for (const no of pontos) {
      const alerta = emAlerta(no);
      L.circleMarker([no.lat!, no.lng!], {
        radius: no.raiz && destacar ? 9 : 7,
        color: alerta ? CORES_GRAFO.alerta : '#ffffff',
        weight: alerta ? 3 : 1.5,
        fillColor: no.raiz && destacar ? CORES_GRAFO.raiz : CORES_GRAFO.empresa,
        fillOpacity: 0.9,
      })
        .bindPopup(() => this.popup(no))
        .addTo(camada);
    }

    const coords = pontos.map(n => [n.lat!, n.lng!] as [number, number]);
    if (coords.length === 1) this.mapa.setView(coords[0], 12);
    else this.mapa.fitBounds(coords, { padding: [40, 40] });
    // O container pode ter mudado de tamanho (tela cheia) desde que o mapa nasceu.
    setTimeout(() => this.mapa?.invalidateSize(), 0);
  }

  private popup(no: NoGrafoResponse): HTMLElement {
    const div = document.createElement('div');
    div.className = 'registry-geo__popup';
    const linha = (texto: string, classe: string): HTMLDivElement => {
      const p = document.createElement('div');
      p.className = classe;
      p.textContent = texto;
      div.appendChild(p);
      return p;
    };
    linha(no.raiz ? 'Empresa consultada' : 'Empresa ligada', 'registry-geo__eyebrow');
    linha(no.rotulo, 'registry-geo__name');
    if (no.cnpj) linha(CnpjFormatter.format(no.cnpj), 'registry-geo__doc');
    if (no.situacao) {
      const p = linha(`Situação: ${no.situacao}`, 'registry-geo__line');
      if (emAlerta(no)) p.classList.add('registry-geo__line--alert');
    }
    const local = [no.municipio, no.uf].filter(Boolean).join('/');
    if (local) linha(local, 'registry-geo__line');
    linha(
      no.origemCoord === 'endereco' ? '📍 endereço exato' : '📍 centro da cidade (aprox.)',
      'registry-geo__hint'
    );
    return div;
  }
}
